<template>
  <div
    class="w-full border-b border-neutral-secondary rounded-t-sm bg-neutral-primary"
  >
    <div class="px-3 pt-12 pb-2">
      <div class="relative" dir="ltr">
        <div
          v-if="
            lowerPriceThreshold <= lastPrice && lastPrice <= upperPriceThreshold
          "
          class="absolute z-[2] bottom-3/4 flex flex-col items-center transform -translate-x-1/2 transition-all"
          :style="{ left: `${styles.lastPricePointer}%` }"
          :class="{
            'text-critical-primary': lastPrice < referencePrice,
            'text-neutral-secondary': lastPrice === referencePrice,
            'text-brand-primary': lastPrice > referencePrice,
          }"
        >
          <NumberFormat #="{ number }" :value="lastPrice">
            <NText
              :variant="variant"
              weight="bold"
              :class="{
                'absolute left-0.5 bottom-full': styles.lastPricePointer < 4,
                'absolute right-0.5 bottom-full': styles.lastPricePointer > 95,
              }"
            >
              {{ number }}
            </NText>
          </NumberFormat>

          <NegarVariantIcon size="18" />

          <NPopper hover>
            <i
              class="inline-block rounded-full h-2.5 w-2.5 border-2 border-neutral-tertiary"
              :class="{
                'bg-critical-primary': lastPrice < referencePrice,
                'bg-neutral-secondary': lastPrice === referencePrice,
                'bg-brand-primary': lastPrice > referencePrice,
              }"
            />

            <template #content>
              <div
                class="flex justify-center bg-neutral-quaternary py-1 rounded-md w-20"
              >
                <NText
                  variant="caption2"
                  weight="medium"
                  class="text-neutral-quaternary"
                >
                  {{ t('symbol.last_trade_price') }}
                </NText>
              </div>
            </template>
          </NPopper>
        </div>

        <div
          v-if="
            lowerPriceThreshold <= referencePrice &&
            upperPriceThreshold >= referencePrice
          "
          class="absolute h-1 top-0"
          :style="{ width: `${styles.referencePricePointer}%` }"
          :class="{ 'bg-critical-primary': Boolean(styles.tradeRange) }"
        >
          <NPopper placement="top" hover>
            <div
              class="leading-none ml-[100%] flex flex-col items-center text-center transform -translate-x-1/2 absolute -top-1.5 z-[2]"
            >
              <i
                class="inline-block rounded-full bg-neutral-secondary h-2.5 w-2.5 border-2 border-neutral-tertiary mt-[3px]"
              />
            </div>

            <template #content>
              <div
                class="flex items-center justify-center flex-nowrap bg-neutral-quaternary px-2 py-1 rounded-md w-32"
              >
                <NumberFormat #="{ number }" :value="previousDayPrice">
                  <NText
                    class="text-neutral-quaternary me-1"
                    variant="overline"
                    weight="medium"
                    dir="ltr"
                  >
                    {{ number }}
                  </NText>
                </NumberFormat>
                <NText
                  variant="caption2"
                  weight="medium"
                  class="text-neutral-quaternary"
                >
                  {{ ' : ' + t('symbol.yesterday_price') }}
                </NText>
              </div>
            </template>
          </NPopper>
        </div>

        <NPopper
          v-if="
            lowerPriceThreshold <= lowestTradePrice &&
            upperPriceThreshold >= lowestTradePrice
          "
          placement="top"
          hover
          offset-y="5"
        >
          <div
            class="w-0.5 h-1.5 -top-[1px] transition-all absolute z-[2]"
            :style="{ left: `${styles.leftGap}%` }"
            :class="{
              'bg-critical-primary': lowestTradePrice < referencePrice,
              'bg-neutral-secondary': lowestTradePrice === referencePrice,
              'bg-brand-primary': lowestTradePrice > referencePrice,
            }"
          />

          <template #content>
            <div
              class="flex justify-center items-center bg-neutral-quaternary py-1 px-2 rounded-md"
            >
              <NumberFormat #="{ number }" :value="lowestTradePrice">
                <NText
                  class="text-neutral-quaternary"
                  variant="overline"
                  weight="medium"
                >
                  {{ number }}
                </NText>
              </NumberFormat>
              <NText
                variant="caption2"
                weight="medium"
                class="text-neutral-quaternary"
              >
                {{ ' : ' + t('symbol.minimumPrice') }}
              </NText>
            </div>
          </template>
        </NPopper>

        <NPopper
          v-if="
            lowerPriceThreshold <= highestTradePrice &&
            upperPriceThreshold >= highestTradePrice
          "
          placement="top"
          hover
          offset-y="5"
        >
          <div
            class="w-0.5 h-1.5 -top-[1px] transition-all absolute z-[2]"
            :style="{ right: `${styles.rightGap}%` }"
            :class="{
              'bg-critical-primary': highestTradePrice < referencePrice,
              'bg-neutral-secondary': highestTradePrice === referencePrice,
              'bg-brand-primary': highestTradePrice > referencePrice,
            }"
          />

          <template #content>
            <div
              class="flex justify-center items-center bg-neutral-quaternary py-1 px-2 rounded-md"
            >
              <NumberFormat #="{ number }" :value="highestTradePrice">
                <NText
                  class="text-neutral-quaternary"
                  variant="overline"
                  weight="medium"
                >
                  {{ number }}
                </NText>
              </NumberFormat>
              <NText
                variant="caption2"
                weight="medium"
                class="text-neutral-quaternary"
              >
                {{ ' : ' + t('symbol.maximumPrice') }}
              </NText>
            </div>
          </template>
        </NPopper>

        <div class="bg-neutral-secondary-hover flex items-center">
          <div
            class="bg-neutral-secondary-hover h-1 z-[1] transition-all"
            :style="{ width: `${styles.leftGap}%` }"
          />
          <div
            class="h-1 bg-brand-primary"
            :style="{ width: `${styles.tradeRange}%` }"
          />
          <div
            class="bg-neutral-secondary-hover h-1 z-[1] transition-all"
            :style="{ width: `${styles.rightGap}%` }"
          />
        </div>

        <div class="flex justify-between mt-1.5">
          <NumberFormat #="{ number }" :value="lowerPriceThreshold">
            <NText
              class="text-neutral-secondary"
              variant="overline"
              weight="medium"
            >
              {{ number }}
            </NText>
          </NumberFormat>

          <NText
            weight="medium"
            variant="overline"
            class="text-neutral-secondary"
          >
            {{ t('symbol.allowed_daily_interval') }}
          </NText>

          <NumberFormat #="{ number }" :value="upperPriceThreshold">
            <NText
              class="text-neutral-secondary"
              variant="overline"
              weight="medium"
            >
              {{ number }}
            </NText>
          </NumberFormat>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { NegarVariantIcon } from '@negar/icons';
import { isOptionSymbol } from '@core/symbol';

import NumberFormat from '@/components/Number.vue';

const props = defineProps({
  lowerPriceThreshold: {
    type: Number,
    required: true,
  },
  instrumentTypeCode: {
    type: String,
    required: true,
  },
  upperPriceThreshold: {
    type: Number,
    required: true,
  },
  lowestTradePrice: {
    type: Number,
    required: true,
  },
  highestTradePrice: {
    type: Number,
    required: true,
  },
  lastPrice: {
    type: Number,
    required: true,
  },
  referencePrice: {
    type: Number,
    required: true,
  },
  elevation: {
    type: String,
    default: 'medium',
  },
  size: {
    type: String,
    required: true,
  },
  previousDayPrice: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();

const variant = computed(() =>
  props.size === 'small' ? 'overline' : 'caption1'
);

const isOption = computed(() => isOptionSymbol(props.instrumentTypeCode));

const styles = computed(() => {
  let range;
  let maxRange;

  if (isOption.value) {
    const maxTradePrice = Math.max(
      Math.abs(props.highestTradePrice),
      Math.abs(props.lowestTradePrice)
    );

    if (maxTradePrice < props.referencePrice) {
      range = (props.referencePrice - props.lowerPriceThreshold) * 2;
    } else {
      const rangeScale =
        maxTradePrice % props.referencePrice === 0
          ? maxTradePrice / props.referencePrice + 1
          : Math.ceil(maxTradePrice / props.referencePrice);

      range = (props.referencePrice - props.lowerPriceThreshold) * rangeScale;
    }

    maxRange = range;
  } else {
    range = props.upperPriceThreshold - props.lowerPriceThreshold;
    maxRange = props.upperPriceThreshold;
  }

  const tradeRange =
    ((props.highestTradePrice - props.lowestTradePrice) / range) * 100;

  const rightGap = ((maxRange - props.highestTradePrice) / range) * 100;

  const leftGap =
    ((props.lowestTradePrice - props.lowerPriceThreshold) / range) * 100;

  const referencePricePointer =
    ((props.referencePrice - props.lowerPriceThreshold) / range) * 100;

  const lastPricePointer =
    ((props.lastPrice - props.lowerPriceThreshold) / range) * 100;

  return {
    tradeRange,
    leftGap,
    rightGap,
    referencePricePointer,
    lastPricePointer,
  };
});
</script>
